import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { isTeacher } from '../../../utils/firebase';
import { connectKioskWithClassCode, getStoredKioskSession } from '../../../utils/kioskAuth';
import InputField from '../../../components/ui/InputField';
import Button from '../../../components/ui/Button';

const KioskConnect: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const stored = getStoredKioskSession();

  const [classCode, setClassCode] = useState(stored?.classCode ?? '');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser && isTeacher(currentUser)) {
      navigate('/class/teacher/SeatDisplay', { replace: true });
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedCode = classCode.trim().toUpperCase();
    const trimmedPin = pin.trim();
    if (!trimmedCode) {
      setError('학급 코드를 입력해 주세요.');
      return;
    }
    if (!/^\d{4,6}$/.test(trimmedPin)) {
      setError('PIN은 4~6자리 숫자입니다.');
      return;
    }

    setLoading(true);
    try {
      await connectKioskWithClassCode(trimmedCode, trimmedPin);
      navigate('/class/teacher/SeatDisplay', { replace: true });
    } catch (err) {
      console.error('키오스크 연동 오류:', err);
      const message =
        err instanceof Error
          ? err.message
          : typeof (err as { message?: string })?.message === 'string'
            ? (err as { message: string }).message
            : '연동에 실패했습니다. 학급 코드와 PIN을 확인해 주세요.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (currentUser && isTeacher(currentUser)) {
    return <Navigate to="/class/teacher/SeatDisplay" replace />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'var(--Color-background-main, #F4F6FA)',
        fontFamily: 'var(--font-family-primary, Pretendard)'
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: '420px',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--gap-level-4)',
          padding: '32px 28px',
          borderRadius: '16px',
          background: 'var(--Color-background-normal, white)',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.06)'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <h1
            style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: 600,
              lineHeight: '36px',
              color: 'var(--Color-text-strong, #0D0D0D)'
            }}
          >
            태블릿 자리표 연동
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: '14px',
              lineHeight: '21px',
              color: 'var(--Color-text-normal, #161616)'
            }}
          >
            학급 코드와 태블릿 PIN을 입력하면 교사 로그인 없이 자리표·출결 화면을 사용할 수 있습니다.
          </p>
        </div>

        <InputField
          label="학급 코드"
          value={classCode}
          onChange={setClassCode}
          placeholder="예: A1B2C3"
          size="medium"
        />

        <InputField
          label="태블릿 PIN"
          value={pin}
          onChange={setPin}
          placeholder="4~6자리 숫자"
          size="medium"
          type="password"
        />

        {error && (
          <div
            role="alert"
            style={{
              padding: '12px 14px',
              borderRadius: '8px',
              background: '#FFF5F5',
              color: '#C62828',
              fontSize: '14px',
              lineHeight: '21px'
            }}
          >
            {error}
          </div>
        )}

        <Button variant="primary" size="medium" type="submit" disabled={loading}>
          {loading ? '연동 중…' : '연동하기'}
        </Button>

        <p
          style={{
            margin: 0,
            fontSize: '13px',
            lineHeight: '20px',
            color: 'var(--Color-text-assistive, #989898)',
            textAlign: 'center'
          }}
        >
          PIN은 교사가 학급 관리 → 학급 코드 화면에서 설정합니다.
        </p>
      </form>
    </div>
  );
};

export default KioskConnect;
